import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SeasonService } from '../../../services/firebaseService';
import { ELIMINATION_TYPES } from '../../../models/dataModels';

const schema = yup.object({
  weekNumber: yup.number().required('Week number is required').min(1, 'Week number must be at least 1'),
  airDate: yup.date().required('Air date is required'),
  isFinale: yup.boolean().required(),
  isMergeWeek: yup.boolean().required(),
  isOpenForPredictions: yup.boolean().required(),
  eliminationType: yup
    .string()
    .oneOf([
      ELIMINATION_TYPES.SINGLE,
      ELIMINATION_TYPES.DOUBLE,
      ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS,
      ELIMINATION_TYPES.TRIPLE,
    ])
    .required('Elimination type is required'),
}).required();

export default function WeekModal({ week, seasonId, existingWeeks, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calculate next week number
  const getNextWeekNumber = () => {
    if (existingWeeks.length === 0) return 1;
    const maxWeek = Math.max(...existingWeeks.map(w => w.weekNumber));
    return maxWeek + 1;
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      weekNumber: getNextWeekNumber(),
      airDate: '',
      isFinale: false,
      isMergeWeek: false,
      isOpenForPredictions: true,
      eliminationType: 'single',
    }
  });

  const isFinale = watch('isFinale');
  const isMergeWeek = watch('isMergeWeek');

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');

      // Check if week number already exists
      const weekExists = existingWeeks.some(w => w.weekNumber === data.weekNumber);
      if (weekExists) {
        setError('A week with this number already exists');
        return;
      }

      const newWeek = {
        weekNumber: data.weekNumber,
        airDate: data.airDate,
        isFinale: data.isFinale,
        isMergeWeek: data.isMergeWeek,
        isOpenForPredictions: data.isOpenForPredictions,
        eliminationType: data.eliminationType,
        results: {}
      };

      // Add week to season
      const updatedWeeks = [...existingWeeks, newWeek];
      
      // If this is marked as a merge week, update the season's mergeWeek field
      const updateData = { weeks: updatedWeeks };
      if (data.isMergeWeek) {
        updateData.mergeWeek = data.weekNumber;
      }
      
      await SeasonService.updateSeason(seasonId, updateData);

      onSave();
    } catch (error) {
      console.error('Error saving week:', error);
      setError('Failed to save week. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add Week</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="weekNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Week Number *
            </label>
            <input
              type="number"
              id="weekNumber"
              {...register('weekNumber', { valueAsNumber: true })}
              min="1"
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                errors.weekNumber ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.weekNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.weekNumber.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="airDate" className="block text-sm font-medium text-gray-700 mb-1">
              Air Date *
            </label>
            <input
              type="date"
              id="airDate"
              {...register('airDate')}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                errors.airDate ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.airDate && (
              <p className="mt-1 text-sm text-red-600">{errors.airDate.message}</p>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isMergeWeek"
                {...register('isMergeWeek')}
                className="h-4 w-4 text-survivor-600 focus:ring-survivor-500 border-gray-300 rounded"
              />
              <label htmlFor="isMergeWeek" className="ml-2 block text-sm text-gray-900">
                Merge Week
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Check this if this is the week when the merge occurs
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isFinale"
                {...register('isFinale')}
                className="h-4 w-4 text-survivor-600 focus:ring-survivor-500 border-gray-300 rounded"
              />
              <label htmlFor="isFinale" className="ml-2 block text-sm text-gray-900">
                Finale Week
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Check this if this is the final week of the season
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isOpenForPredictions"
                {...register('isOpenForPredictions')}
                className="h-4 w-4 text-survivor-600 focus:ring-survivor-500 border-gray-300 rounded"
              />
              <label htmlFor="isOpenForPredictions" className="ml-2 block text-sm text-gray-900">
                Open for Predictions
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Allow players to submit predictions for this week
            </p>
          </div>

          <div>
            <label htmlFor="eliminationType" className="block text-sm font-medium text-gray-700 mb-1">
              Elimination Type *
            </label>
            <select
              id="eliminationType"
              {...register('eliminationType')}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                errors.eliminationType ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value={ELIMINATION_TYPES.SINGLE}>Single Elimination</option>
              <option value={ELIMINATION_TYPES.DOUBLE}>Double Elimination</option>
              <option value={ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS}>
                Double Elimination (Simultaneous)
              </option>
              <option value={ELIMINATION_TYPES.TRIPLE}>Triple Elimination</option>
            </select>
            {errors.eliminationType && (
              <p className="mt-1 text-sm text-red-600">{errors.eliminationType.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Single: 1 pick. Double / simultaneous: 2 ordered picks. Triple: 3 ordered picks.
            </p>
          </div>

          {isMergeWeek && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                <strong>Merge Week:</strong> This will mark the transition from pre-merge to post-merge. Prediction points will change accordingly.
              </p>
            </div>
          )}

          {isFinale && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-sm text-yellow-800">
                <strong>Finale Week:</strong> Players will be able to predict the winner, runner-up, and fire-making loser.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Adding...' : 'Add Week'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
